/**
 * Cosmos-DB-aware base fields shared by every content document + the
 * primitive types referenced across the schema.
 *
 * Concurrency model (briefing Q7):
 *   - 1-3 admin users, rarely simultaneous => optimistic concurrency with `_etag`
 *     and HTTP `If-Match` headers is sufficient. No row-locking.
 *
 * Partition strategy (decided in Phase 2, see PLAN.md section A):
 *   - flows         : partitionKey = "/id"        (one doc per flow)
 *   - landing       : partitionKey = "/id"        (one doc per surface)
 *   - audit         : partitionKey = "/yearMonth" (bounded per-month queries)
 *   - users         : partitionKey = "/id"        (projection of Entra app roles)
 *   - submissions   : partitionKey = "/yearMonth" (bounded month-picker queries)
 *   - adoption      : partitionKey = "/toolWeek"  ("<tool>:<isoWeek>" — the scope
 *                     of a transactional batch AND of every read, see adoption.ts)
 *   (N6 removed the review/publish state machine — no `reviews` container.)
 *
 * Validators: every type below has a Zod schema with the same name +
 * `Schema` suffix. The Content API uses these at every write boundary to
 * refuse malformed payloads (audit finding: today we `as FlowConfig`).
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Primitive validators
// ---------------------------------------------------------------------------

/** ISO 8601 timestamp, e.g. `2026-05-28T10:00:00.000Z`. */
export const IsoTimestampSchema = z.string().datetime({ offset: false });
export type IsoTimestamp = z.infer<typeof IsoTimestampSchema>;

/** Cosmos document id — non-empty, URL-safe-ish. */
export const EntityIdSchema = z.string().min(1).max(128);
export type EntityId = z.infer<typeof EntityIdSchema>;

/** Cosmos-managed optimistic-concurrency token. */
export const EtagSchema = z.string().min(1);
export type Etag = z.infer<typeof EtagSchema>;

/** YYYY-MM partition value used by the audit container (e.g. "2026-05"). */
export const YearMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'expected YYYY-MM');
export type YearMonth = z.infer<typeof YearMonthSchema>;

// ---------------------------------------------------------------------------
// URL protocol allowlist (Chapter 10, F18)
// ---------------------------------------------------------------------------

/**
 * Whitespace + ASCII C0 control characters + DEL. Browsers ignore these when
 * resolving a URL scheme, so `java\tscript:` and `\u0000javascript:` both
 * execute — they must be normalised away BEFORE any scheme comparison.
 */
// eslint-disable-next-line no-control-regex
const CONTROL_AND_WHITESPACE = /[\s\u0000-\u001f\u007f]+/g;

/**
 * True when `url` is an ABSOLUTE http(s) URL and nothing else.
 *
 * Deliberately stricter than `isSafeHref` (`site-content.ts`), which also
 * admits relative paths, hash anchors, `mailto:` and `tel:` because it guards
 * *link targets an operator authors for the public site*. This predicate guards
 * a stored **product link** (`Option.internalProductUrl`), where those forms are
 * either useless or actively misleading:
 *
 *   - `javascript:` / `data:` / `vbscript:` — the admin renders the value as a
 *     clickable link for a colleague, so a stored script URL is stored XSS
 *     against our own team. Rejected at WRITE time so it can never persist.
 *   - a relative path — would resolve against the admin's own origin, silently
 *     pointing the operator back at the platform instead of the supplier.
 *   - `mailto:` / `tel:` — not a place you can look up "what item we used".
 *
 * Whitespace and ASCII control characters are stripped before the scheme test,
 * because browsers ignore them when resolving a scheme: `java\tscript:` and
 * `\0javascript:` both execute. Same normalisation as `isSafeHref`.
 */
export function isHttpUrl(url: string): boolean {
  const cleaned = url.replace(CONTROL_AND_WHITESPACE, '').toLowerCase();
  if (!cleaned.startsWith('http://') && !cleaned.startsWith('https://')) {
    return false;
  }
  // A scheme alone is not a URL — require a host after it.
  try {
    const parsed = new URL(cleaned);
    return parsed.hostname.length > 0;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Domain enums
// ---------------------------------------------------------------------------

export const RoleSchema = z.enum(['admin', 'editor', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

/**
 * Content lifecycle status.
 *
 * Simplified to `draft ↔ published` (N6, 2026-06-09): the draft→review→publish
 * approval workflow was removed. An authorized user (admin/editor) edits a
 * draft and publishes it directly — there is no separate review step.
 *
 * `archived` is a soft-delete state (admin "archive" action) so we never lose
 * published history. Public reads only return `published`.
 */
export const PublishStatusSchema = z.enum([
  'draft',
  'published',
  'archived',
]);
export type PublishStatus = z.infer<typeof PublishStatusSchema>;

// Backwards-compatible alias — pre-Zod code (mockData.ts) referred to this as
// ContentStatus.
export const ContentStatusSchema = PublishStatusSchema;
export type ContentStatus = PublishStatus;

// ---------------------------------------------------------------------------
// Composite types
// ---------------------------------------------------------------------------

/** Free-form audit-friendly user reference. */
export const UserRefSchema = z.object({
  id: EntityIdSchema,
  email: z.string().email(),
  /** Display name (may be omitted for system / migration actors). */
  name: z.string().min(1).max(256).optional(),
});
export type UserRef = z.infer<typeof UserRefSchema>;

/**
 * Cosmos-managed metadata fields. All optional in our types because we omit
 * them on writes (Cosmos sets them) and require them only on reads.
 */
export const CosmosBaseSchema = z.object({
  id: EntityIdSchema,
  _etag: EtagSchema.optional(),
  _ts: z.number().int().nonnegative().optional(),
});
export type CosmosBase = z.infer<typeof CosmosBaseSchema>;
